import { useState } from 'react';
import { Typography, Popover } from '@mui/material';
import styled from 'styled-components';
import { Operator, UrlFilters } from '../../../hooks/useUrlFilters';
import Button from '../../Button';
import FilterChip from '../../FilterChip';
import { FilterOption, OnSubmitEventVal } from '../../FilterSelector';
import FilterSelector from '../../FilterSelector';
import { Flex } from '../../Flex';
import { ColumnType } from '../types';

const PopoverStyled = styled(Popover)`
  .MuiPaper-root {
    min-width: 700px;
    padding: 24px;
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
    border-radius: 6px;
    border: 1px solid ${({ theme }) => theme.palette.common.gray8};
  }
`;

type Props = {
  filterOptions: FilterOption[];
  selectedFilters?: UrlFilters;
  onSubmit?: (val: OnSubmitEventVal) => void;
  onAddFilter?: (val: OnSubmitEventVal) => void;
  clearFilter: (
    columnId: string,
    relationship: Operator,
    value?: string[],
  ) => void;
  onClearAll?: () => void;
  getLabel?: (property: string) => string | undefined;
  getEmptyValueLabel?: (property: string) => string;
  getType?: (property: string) => ColumnType;
};

const FilterChipRow = ({
  filterOptions,
  selectedFilters = [],
  clearFilter,
  onClearAll,
  getLabel,
  getEmptyValueLabel,
  getType,
  onSubmit,
  onAddFilter,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  if (!selectedFilters?.length && !onAddFilter) {
    return null;
  }

  const getOptionLabel = (property: string) =>
    filterOptions.find((o) => o.property === property)?.propertyLabel;

  return (
    <Flex sx={{ gap: 1, flexWrap: 'wrap' }}>
      <Flex
        sx={{ gap: 1, flexWrap: 'wrap', alignItems: 'center' }}
        divider={
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 'bold',
              color: 'common.gray2',
            }}
          >
            AND
          </Typography>
        }
      >
        {selectedFilters.map((filter, i) => (
          <FilterChip
            key={`${filter.operator}-${filter.property}-${i}`}
            propertyLabel={
              getLabel?.(filter.property) ||
              getOptionLabel(filter.property) ||
              filter.property
            }
            emptyValueLabel={getEmptyValueLabel?.(filter.property)}
            type={getType?.(filter.property)}
            property={filter.property}
            relationship={filter.operator}
            value={filter.value}
            index={i}
            onDeleteClick={() =>
              clearFilter(filter.property, filter.operator, filter.value)
            }
            onSubmit={onSubmit}
            filterOptions={filterOptions}
          />
        ))}
      </Flex>
      {onAddFilter && selectedFilters.length > 0 && (
        <Button color="secondary" onClick={(e) => setAnchorEl(e.currentTarget)}>
          Add filter
        </Button>
      )}
      {selectedFilters.length > 0 && !!onClearAll && (
        <Button color="secondary" onClick={onClearAll}>
          {`Clear filters (${selectedFilters.length})`}
        </Button>
      )}

      {onAddFilter && (
        <PopoverStyled
          anchorEl={anchorEl}
          open={!!anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: 50,
            horizontal: 'left',
          }}
        >
          <Typography sx={{ fontWeight: 'bold', mb: 3 }} variant="body1">
            Add filter
          </Typography>

          <FilterSelector
            filterOptions={filterOptions}
            onSubmit={(val) => {
              onAddFilter(val);
              setAnchorEl(null);
            }}
            onClose={() => setAnchorEl(null)}
            multiselect
          />
        </PopoverStyled>
      )}
    </Flex>
  );
};

export default FilterChipRow;
